import React, { useState, useEffect, useCallback } from "react";
import { StyleSheet, Text, View, ScrollView } from "react-native";
import { colors } from "src/assets";
import {
  BottomActions,
  Button,
  UserCard,
  CallDataRow,
  CallOffer,
  Typography,
} from "src/components";
import { useNavigation, useRoute, RouteProp } from "@react-navigation/native";
import { StackNavigationProp } from "@react-navigation/stack";
import { CustomTextEditNavigationParam } from "src/screens/custom-text-edit";
import {
  navigationRef,
  useOffer,
  useUser,
  useAccount,
  useCallActions,
} from "src/hooks";
import { UserNavigationParam } from "src/screens/user";
import { BalanceNavigationParam } from "src/screens/balance";
import { EventRegister } from "react-native-event-listeners";
import { v4 as uuid } from "uuid";
import { ErrorDialog, IError } from "src/components";
import LocalText from "src/local";

const PriceControl: React.FC<{ price: number }> = ({ price }) => {
  return (
    <CallDataRow
      text={LocalText.screens.callRequest.price}
      detail={`¥${price} / 1分`}
    />
  );
};

const MaxTimeControl: React.FC<{ price: number; balance?: number }> = ({
  price,
  balance,
}) => {
  const getDetail = () => {
    let maxTime = LocalText.screens.callRequest.balanceExpired;
    if (balance && balance >= price) {
      maxTime = `${Math.floor(balance / price) + 1}分`; // With free 1 min period
    }
    return maxTime;
  };

  return (
    <CallDataRow
      text={LocalText.screens.callRequest.maxTalkTime}
      detail={getDetail()}
    />
  );
};

const WaitingTimeControl: React.FC<{
  value: number;
  onChange: (value: number) => void;
}> = ({ value, onChange }) => {
  const eventName = "CallRequestMaxTimeEvent";
  useEffect(() => {
    const listener = EventRegister.addEventListener(eventName, (v: string) => {
      onChange(parseInt(v));
    });
    return () => {
      EventRegister.removeEventListener(listener as string);
    };
  }, [onChange]);
  return (
    <CallDataRow
      text={LocalText.screens.callRequest.waitingTime}
      detail={`${value}分`}
      rightIcon={"chevron"}
      onPress={() => {
        navigationRef.current?.navigate("CustomSelectionList", {
          eventName: "CallRequestMaxTimeEvent",
          title: LocalText.screens.callRequest.waitingTime,
          selectedId: `${value}`,
          items: [5, 10, 15, 30, 60, 120].map((item) => ({
            id: `${item}`,
            label: `${item}分`,
          })),
        });
      }}
    />
  );
};

const DescriptionControl: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => {
  const eventName = "CallRequestCommentEvent";
  useEffect(() => {
    const listener = EventRegister.addEventListener(eventName, (v: string) => {
      onChange(v);
    });
    return () => {
      EventRegister.removeEventListener(listener as string);
    };
  }, [onChange]);
  return (
    <CallDataRow
      leftIcon={"comment"}
      text={value ? value : LocalText.screens.callRequest.commentPlaceholder}
      rightIcon={"chevron"}
      onPress={() => {
        navigationRef.current?.navigate("CustomTextEdit", {
          eventName: eventName,
          title: LocalText.screens.callRequest.comment,
          placeholder: LocalText.screens.callRequest.comment,
          text: value,
          isMultiline: true,
        });
      }}
    />
  );
};

export type CallRequestNavigationParam = {
  offerId: string;
};

export type StackParamList = {
  CallRequest: CallRequestNavigationParam;
  CustomTextEdit: CustomTextEditNavigationParam;
  User: UserNavigationParam;
  Balance: BalanceNavigationParam;
};

export const CallRequestScreen: React.FC = () => {
  const navigation =
    useNavigation<StackNavigationProp<StackParamList, "CallRequest">>();
  const route = useRoute<RouteProp<StackParamList, "CallRequest">>();
  const [waitingTime, setWaitingTime] = useState<number>(30);
  const [message, setMessage] = useState<string>("");
  const [offer] = useOffer(route.params.offerId);
  const [owner] = useUser(offer?.owner?.id);
  const [account] = useAccount();
  const { createCallRequest } = useCallActions();
  const [error, setError] = useState<IError | undefined>();
  const [processing, setProcessing] = useState<boolean>(false);

  const onCreate = useCallback(async () => {
    setProcessing(true);
    try {
      await createCallRequest({
        input: {
          id: uuid() as string,
          serviceId: route.params.offerId,
          waitingTime,
          message,
          requestTimestamp: "" + Date.now(),
        },
      });
      navigation.goBack();
    } catch (e: any) {
      console.error(e);
      if (e.errors) {
        setError({ message: e.errors[0].message });
      } else {
        setError(e as Error);
      }
    }
    setProcessing(false);
  }, [waitingTime, message, route]);

  return (
    <View style={styles.container}>
      {offer ? (
        <React.Fragment>
          <ScrollView
            style={styles.scrollView}
            showsVerticalScrollIndicator={false}
          >
            {owner ? (
              <UserCard
                contanerStyle={styles.owner}
                source={owner?.image ? { uri: owner?.image } : undefined}
                status={offer?.owner?.status}
                name={owner?.name}
                birthday={owner?.birthday}
                gender={owner?.gender}
                prefecture={owner?.address?.prefecture}
                isVerified={owner?.identificationStatus === "APPROVED"}
                onPress={() =>
                  navigation.push("User", { id: owner.id, title: "" })
                }
              />
            ) : null}
            {offer ? (
              <React.Fragment>
                <Typography
                  text={LocalText.screens.callRequest.service}
                  variant={"section"}
                />
                <CallOffer offer={offer} />
                <PriceControl price={offer.price || 0} />
                {account ? (
                  <MaxTimeControl
                    price={offer.price || 0}
                    balance={account?.balance}
                  />
                ) : null}
              </React.Fragment>
            ) : null}
            <View style={{ paddingTop: 16, paddingHorizontal: 8 }}>
              <Button
                variant={"primary"}
                title={LocalText.screens.callRequest.buyPoints}
                containerStyle={{
                  marginLeft: "auto",
                  marginRight: "auto",
                  paddingHorizontal: 16,
                }}
                onPress={() => navigation.push("Balance")}
              />
            </View>
            <Typography
              text={LocalText.screens.callRequest.aboutTalk}
              variant={"section"}
            />
            <WaitingTimeControl
              value={waitingTime}
              onChange={(v) => setWaitingTime(v)}
            />
            <DescriptionControl
              value={message}
              onChange={(v) => setMessage(v)}
            />
          </ScrollView>
          <BottomActions>
            <Button
              variant={"secondary"}
              icon={"cancel"}
              iconColor={colors.chokuRed}
              title={LocalText.common.cancel}
              onPress={() => navigation.goBack()}
            />
            <View style={styles.offerActionContainer}>
              <Button
                variant={"primary"}
                icon={"send"}
                title={LocalText.screens.callRequest.send}
                disabled={
                  !(account?.balance && account?.balance >= (offer?.price || 0))
                }
                loading={processing}
                onPress={onCreate}
              />
              <Text style={styles.price}>
                ¥<Text style={styles.priceBold}>{offer?.price}</Text> /{" "}
                <Text style={styles.priceBold}>1</Text>分
              </Text>
            </View>
          </BottomActions>
        </React.Fragment>
      ) : null}
      {error ? (
        <ErrorDialog
          error={error}
          visible={true}
          onClose={() => setError(undefined)}
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: "column",
    backgroundColor: colors.white,
  },
  scrollView: {
    flex: 1,
  },
  owner: {
    marginTop: 16,
    marginBottom: 8,
    marginHorizontal: 16,
  },
  info: {
    fontSize: 12,
    color: colors.chokuBlack,
    padding: 16,
  },
  infoBold: {
    fontWeight: "500",
    color: "#666666",
  },
  offerActionContainer: {
    flex: 1,
    marginLeft: 8,
  },
  price: {
    textAlign: "center",
    fontSize: 12,
    color: colors.chokuRed,
  },
  priceBold: {
    fontSize: 14,
    fontWeight: "bold",
  },
});
